import { useCallback, useEffect, useState } from "react";

export const useForm = (initialState) => {
  const [form, setForm] = useState(initialState);
  const [formData, setValue] = useState({ ...initialState });

  const requiredRule = useCallback((inputName) => {
    return {
      name: "required",
      message: `${inputName} is required`,
      validate: (inputValue, formObj) => inputValue.length !== 0,
    };
  }, []);

  const handleChange = useCallback((values) => {
    setForm((f) => ({ ...f, ...values }));
  }, []);

  const handleValueChange = useCallback((values) => {
    setValue((prev) => ({ ...prev, ...values }));
  }, []);

  const isInputFieldValid = useCallback(
    (inputField) => {
      for (const rule of inputField.validationRules) {
        if (!rule.validate(inputField.value, form)) {
          inputField.errorMessage = rule.message;
          return false;
        }
      }
      return true;
    },
    [form]
  );

  const onChange = useCallback(
    (name, value) => {
      const inputObj = { ...form[name] };
      inputObj.value = value;
      const isValidInput = isInputFieldValid(inputObj);
      if (isValidInput && !inputObj.valid) {
        inputObj.valid = true;
      } else if (!isValidInput && inputObj.valid) {
        inputObj.valid = false;
      }
      inputObj.touched = true;
      handleChange({ ...form, [name]: inputObj });
      handleValueChange({ ...formData, [name]: value });
    },
    [form, isInputFieldValid, handleChange, handleValueChange, formData]
  );

  const isFormValid = useCallback(() => {
    let isValid = true;
    const arr = Object.values(form);
    for (let i = 0; i < arr.length; i++) {
      if (!arr[i].valid) {
        isValid = false;
        break;
      }
    }
    return isValid;
  }, [form]);

  const validateOnSubmit = useCallback(() => {
    Object.entries(form).forEach(([key, value]) => {
      form[key] = { ...value, touched: true };
    });
  }, [form]);

  useEffect(() => {
    Object.entries(form).forEach(([key, value]) => {
      let inputObj;
      if (value) {
        inputObj = { value: form[key] };
        handleValueChange({ ...formData, [key]: value });
        inputObj.valid = true;
      } else {
        inputObj = {};
        inputObj.valid = false;
      }
      inputObj.value = value;
      inputObj.touched = false;
      inputObj["validationRules"] = [requiredRule(key)];
      isInputFieldValid(inputObj);

      form[key] = inputObj;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  return { form, formData, onChange, isFormValid, validateOnSubmit };
};
